import React, { useState, useEffect, Fragment } from 'react';

import clsx from 'clsx';
import { Form } from 'react-final-form';
import {
  required,
  email,
  useDataProvider,
  useNotify,
  useRefresh,
  cacheDataProviderProxy,
} from 'react-admin';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
} from '@material-ui/core';
import {
  // ReferenceInput,
  FormTextField,
  // SelectComponent,
  // FileInput,
  composeValidators,
  minLength,
  handleNumbers,
  getDateInputValue,
  validateEmailOnSubmit,
  minReasonLength,
  SwitchInput,
  normalizePhone,
} from '.';
import { useStyles } from '../modal.styles';
import { SaveButton } from '../../../../design';
import { useGetUserRoles } from '../../../../hooks/useGetUserRoles';

export const PayerRepForm = ({
  caseId,
  record = {},
  isEdit,
  handleClose,
  closeAside,
  payerFacilityId,
}) => {
  const classes = useStyles();
  const refresh = useRefresh();
  const { is_admin, is_contract_user, is_supervisor } = useGetUserRoles();
  const canToggleActive = is_admin || is_contract_user || is_supervisor;
  const dataProvider = cacheDataProviderProxy(useDataProvider());
  const notify = useNotify();
  const [formData, setFormData] = useState({
    rep_payer_facility_id: payerFacilityId,
    is_active: true,
  });
  const [matchingReps, setMatchingReps] = useState([]);
  const [pendingCreateData, setPendingCreateData] = useState();
  useEffect(() => {
    if (isEdit && record) {
      const { id, ...rest } = record;
      setFormData(f => ({
        ...f,
        ...rest,
      }));
    }
  }, [isEdit, record]);

  const notifyError = error =>
    notify(
      typeof error === 'string'
        ? error
        : error.message || 'ra.notification.http_error',
      'warning',
    );

  const closeMatchingDialog = () => {
    setMatchingReps([]);
    setPendingCreateData(undefined);
  };

  const handleCreateSuccess = (data, submittedData) => {
    if (data?.selection_required) {
      setMatchingReps(Array.isArray(data.matches) ? data.matches : []);
      setPendingCreateData(submittedData);
      return;
    }
    closeMatchingDialog();
    notify(data?.linked_existing ? 'Existing rep linked' : 'form.created');
    handleClose();
    refresh();
  };

  const submitCreate = submittedData =>
    dataProvider
      .create('payers-facility/payer-reps', { data: { ...submittedData } })
      .then(({ data }) => handleCreateSuccess(data, submittedData))
      .catch(notifyError);

  const formatMatchDetails = rep => {
    const phone = rep.phone
      ? `${normalizePhone(rep.phone)}${
          rep.phone_ext ? ` x${rep.phone_ext}` : ''
        }`
      : null;
    const fax = rep.fax ? normalizePhone(rep.fax) : null;
    return [
      rep.email ? `Email: ${rep.email}` : null,
      phone ? `Phone: ${phone}` : null,
      fax ? `Fax: ${fax}` : null,
      rep.already_linked ? 'Already linked to this payer facility' : null,
    ]
      .filter(Boolean)
      .join(' | ');
  };

  const handleUseExistingRep = repId => {
    if (!(repId > 0) || !pendingCreateData) return Promise.resolve();
    return submitCreate({
      ...pendingCreateData,
      rep_id: repId,
    });
  };

  const handleCreateDuplicate = () => {
    if (!pendingCreateData) return Promise.resolve();
    return submitCreate({
      ...pendingCreateData,
      confirm_create_duplicate: true,
    });
  };

  const onSubmit = value => {
    const { email } = value;
    const data = {
      ...value,
      email: validateEmailOnSubmit(email),
    };

    if (isEdit) {
      return dataProvider
        .update('payers-facility/payer-reps', {
          id: record.id,
          data: { ...data },
          previousData: { ...record },
        })
        .then(({ data }) => {
          notify('form.updated');
          handleClose();
          refresh();
        })
        .catch(notifyError);
    } else {
      return submitCreate(data);
    }
  };
  const customOnChange = async (eventOrValue, _name, type) => {
    // when using a react-admin input, onChange returns
    // the value instead of the target.
    let value = eventOrValue;
    let name = _name;
    if (typeof eventOrValue === 'object') {
      const target = eventOrValue.target;
      value = target.type === 'checkbox' ? target.checked : target.value;
      name = target.name;
    }
    if (type === 'number') {
      value = handleNumbers(value);
    }
    if (type === 'date') {
      value = getDateInputValue(value);
    }

    setFormData({ ...formData, [name]: value });
  };
  return (
    <Fragment>
      <Form
        onSubmit={onSubmit}
        initialValues={{
          // ...caseFields,
          ...formData,
        }}
        render={({
          submitError,
          handleSubmit,
          form,
          submitting,
          pristine,
          values,
        }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <div className={clsx(classes.inputContainerWrap)}>
                  <FormTextField
                    customOnChange={customOnChange}
                    name='name'
                    validate={required()}
                    required
                    label='Insurance case manager name'
                  />
                  <FormTextField
                    customOnChange={customOnChange}
                    name='phone'
                    type='text'
                    format='phone'
                    validate={composeValidators([
                      required(),
                      minLength(10, 'Invalid phone number'),
                    ])}
                    required
                    label='Insurance case manager phone'
                  />
                </div>
                <div className={clsx(classes.inputContainerWrap)}>
                  <FormTextField
                    customOnChange={customOnChange}
                    name='phone_ext'
                    label='Ext.'
                  />
                  <FormTextField
                    customOnChange={customOnChange}
                    name='fax'
                    validate={composeValidators([
                      minLength(10, 'Invalid phone number'),
                    ])}
                    type='text'
                    format='phone'
                    label='Insurance case manager fax'
                  />
                </div>
                <div className={clsx(classes.inputContainerWrap)}>
                  <FormTextField
                    customOnChange={customOnChange}
                    name='email'
                    validate={composeValidators([email()])}
                    label='Insurance case manager email'
                    type='email'
                    style={{ marginRight: 0 }}
                  />
                </div>
                {canToggleActive && (
                  <SwitchInput
                    name='is_active'
                    customOnChange={customOnChange}
                    label={formData.is_active ? 'Active' : 'Inactive'}
                    checked={formData.is_active}
                  />
                )}
                <FormTextField
                  name='comments'
                  label='Insurance case manager notes'
                  multiline
                  fullWidth
                  customOnChange={customOnChange}
                  validate={minReasonLength()}
                />
                {isEdit && (
                  <DialogContentText style={{ color: 'orange' }}>
                    Warning: editing this note will change it wherever it is
                    linked.
                  </DialogContentText>
                )}
              </DialogContent>
              <DialogActions
                className={classes.padding16}
                style={{ paddingBottom: 16 }}
              >
                <SaveButton
                  // onClick={handleClose}
                  className={classes.saveButton}
                  disabled={submitting}
                  type='submit'
                />
              </DialogActions>
            </form>
          );
        }}
      />
      <Dialog
        open={matchingReps.length > 0 && !!pendingCreateData}
        onClose={closeMatchingDialog}
        fullWidth
        maxWidth='md'
      >
        <DialogTitle>Possible matching reps</DialogTitle>
        <DialogContent>
          <DialogContentText>
            We found reps that may already exist. You can link one of them to
            this payer facility, or create a new rep anyway.
          </DialogContentText>
          <List>
            {matchingReps.map(rep => (
              <ListItem key={rep.id} divider>
                <ListItemText
                  primary={rep.name || `Rep ${rep.id}`}
                  secondary={formatMatchDetails(rep)}
                />
                <Button
                  color='primary'
                  onClick={() => handleUseExistingRep(rep.id)}
                >
                  Use this rep
                </Button>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMatchingDialog}>Go back</Button>
          <Button color='primary' onClick={handleCreateDuplicate}>
            Create new anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};
